export type SessionState = 'READY' | 'ACTIVE' | 'PAUSED' | 'ENDED';

export interface SessionData {
  state: SessionState;
  consecutiveThrows: number;
  cumulativeTime: number;
  throwsGoal?: number;
  timeGoal?: number;
  goalReached: boolean;
}

export class SessionTracker {
  private state: SessionState = 'READY';
  private consecutiveThrows = 0;
  private cumulativeTime = 0;
  private throwsGoal?: number;
  private timeGoal?: number;
  private lastUpdateTime = 0;
  private isCurrentlyJuggling = false;
  private sessionStartTime = 0;
  private framesWithoutJuggling = 0;
  private stopThreshold = 120; // 2 seconds at 60fps without juggling to stop timer

  setGoals(throwsGoal?: number, timeGoal?: number) {
    this.throwsGoal = throwsGoal;
    this.timeGoal = timeGoal;
  }

  start() {
    this.state = 'ACTIVE';
    this.sessionStartTime = Date.now();
    this.lastUpdateTime = Date.now();
    console.log('Session started');
  }

  onThrowDetected() {
    if (this.state === 'READY') {
      this.start();
    }

    if (this.state === 'ACTIVE') {
      this.consecutiveThrows++;
      this.framesWithoutJuggling = 0; // Reset counter on throw
      console.log('Throw counted! Total:', this.consecutiveThrows);
    }
  }

  onJugglingDetected() {
    if (this.state === 'READY') {
      this.start();
    }
    this.isCurrentlyJuggling = true;
    this.framesWithoutJuggling = 0; // Reset counter
  }

  onJugglingStopped() {
    this.framesWithoutJuggling++;
    
    // Only stop juggling after sustained absence
    if (this.framesWithoutJuggling > this.stopThreshold) {
      this.isCurrentlyJuggling = false;
    }
  }

  onDropDetected() {
    console.log('Drop detected! Resetting count from', this.consecutiveThrows);
    this.consecutiveThrows = 0;
    this.isCurrentlyJuggling = false;
    this.framesWithoutJuggling = 0;
  }

  update(): SessionData {
    const now = Date.now();

    // Track time continuously once session is active and juggling has started
    if (this.state === 'ACTIVE' && this.sessionStartTime > 0) {
      this.cumulativeTime = (now - this.sessionStartTime) / 1000;
    }
    
    this.lastUpdateTime = now;

    const goalReached = this.checkGoalReached();
    if (goalReached && this.state === 'ACTIVE') {
      this.state = 'ENDED';
    }

    return {
      state: this.state,
      consecutiveThrows: this.consecutiveThrows,
      cumulativeTime: this.cumulativeTime,
      throwsGoal: this.throwsGoal,
      timeGoal: this.timeGoal,
      goalReached
    };
  }

  private checkGoalReached(): boolean {
    if (this.throwsGoal && this.consecutiveThrows >= this.throwsGoal) {
      return true;
    }
    if (this.timeGoal && this.cumulativeTime >= this.timeGoal) {
      return true;
    }
    return false;
  }

  end() {
    this.state = 'ENDED';
  }

  reset() {
    this.state = 'READY';
    this.consecutiveThrows = 0;
    this.cumulativeTime = 0;
    this.throwsGoal = undefined;
    this.timeGoal = undefined;
    this.isCurrentlyJuggling = false;
    this.lastUpdateTime = 0;
    this.sessionStartTime = 0;
    this.framesWithoutJuggling = 0;
  }

  getData(): SessionData {
    return {
      state: this.state,
      consecutiveThrows: this.consecutiveThrows,
      cumulativeTime: this.cumulativeTime,
      throwsGoal: this.throwsGoal,
      timeGoal: this.timeGoal,
      goalReached: this.checkGoalReached()
    };
  }
}
